import {getTestProps} from '@benchling/js-test-utils';
import {createStyles, UnstyledButton} from '@mantine/core';
import React, {useMemo} from 'react';

import InlineBanner, {InlineBannerActionType, InlineBannerType} from 'ui-library/banners/InlineBanner';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import {Container} from 'ui-library/NYC';

import {useAccumulatingChatHistory} from '../ChatListing';
import type {SidepanelChat} from '../ChatRouter';
import RedesignChatRow from './RedesignChatRow';

const useStyles = createStyles(() => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
  },
  // Minimized dropdown: flow at natural height (the dropdown itself scrolls).
  rootNatural: {
    height: 'auto',
  },
  starredSection: {
    flex: 'none',
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  // Recent fills the remaining height; only its list scrolls, so the nav + Starred stay pinned.
  recentSection: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
  },
  recentSectionNatural: {
    flex: 'none',
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  recentScroll: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    // Long chat titles ellipsis within the narrow sidebar; never scroll horizontally.
    overflowX: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  sectionHeader: {
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: '#979799',
    margin: '18px 0 6px 4px',
  },
  viewMore: {
    display: 'block',
    width: '100%',
    textAlign: 'left',
    padding: '0 4px',
    fontSize: 13,
    lineHeight: '20px',
    color: '#3C65FB',
    '&:hover': {
      textDecoration: 'underline',
    },
    '&:disabled': {
      color: '#979799',
      cursor: 'default',
      textDecoration: 'none',
    },
  },
  emptyState: {
    padding: '20px 4px',
    color: '#979799',
    fontSize: 13,
  },
  emptyStateTitle: {
    color: '#1B1B1F',
    fontSize: 15,
    marginBottom: 4,
  },
}));

export interface GroupedChatListingProps {
  selectedChatSession: SidepanelChat | null;
  onSelectChatSession: (chat: SidepanelChat) => void;
  refetchRef: React.MutableRefObject<() => void>;
  /**
   * When true (sidebar), the list fills the available height and only the Recent list scrolls. When
   * false (minimized dropdown), the list flows at its natural height and the dropdown scrolls it.
   */
  fillHeight?: boolean;
}

/**
 * Redesigned chat history list (LLM_ENABLE_CHAT_REDESIGN): groups chats into Starred and Recent
 * sections and replaces the legacy prev/next pager with a single "View more" affordance.
 *
 * Starred grouping is computed from the pages loaded so far; a chat starred beyond the currently
 * loaded pages will not appear under Starred until it is loaded. A dedicated starred query would be
 * needed to make the Starred section complete regardless of pagination.
 */
export default function GroupedChatListing({
  selectedChatSession,
  onSelectChatSession,
  refetchRef,
  fillHeight = true,
}: GroupedChatListingProps): JSX.Element {
  const {classes, cx} = useStyles();
  const {items, isInitialLoading, isError, isLoadingMore, hasMore, loadMore, onStarUpdated, retry} =
    useAccumulatingChatHistory(refetchRef);

  const {starredItems, recentItems} = useMemo(
    () => ({
      starredItems: items.filter((item) => Boolean(item.star?.isStarred)),
      recentItems: items.filter((item) => !item.star?.isStarred),
    }),
    [items]
  );

  if (isError) {
    return (
      <Container size="sidepanel">
        <InlineBanner
          bannerType={InlineBannerType.ERROR}
          text="Error loading chats."
          action={{
            actionType: InlineBannerActionType.CUSTOM,
            actionText: 'Retry',
            actionOnClick: retry,
            rightAlign: true,
          }}
        />
      </Container>
    );
  }

  if (isInitialLoading) {
    return <CenteredHorizontalThrobber />;
  }

  if (items.length === 0) {
    return (
      <div
        className={classes.emptyState}
        {...getTestProps({component: 'GroupedChatListing', element: 'empty'})}
      >
        <p className={classes.emptyStateTitle}>No chats found</p>
        <p>Start a new chat from the navigation above.</p>
      </div>
    );
  }

  const renderItem = (item: (typeof items)[number]): JSX.Element => (
    <RedesignChatRow
      key={item.id ?? 'new-chat'}
      item={item}
      onStarUpdated={onStarUpdated}
      onSelect={onSelectChatSession}
      isSelected={selectedChatSession?.id === item.id}
    />
  );

  return (
    <div
      className={cx(classes.root, !fillHeight && classes.rootNatural)}
      {...getTestProps({component: 'GroupedChatListing', element: 'root'})}
    >
      {starredItems.length > 0 && (
        <div className={classes.starredSection}>
          <div
            className={classes.sectionHeader}
            {...getTestProps({component: 'GroupedChatListing', element: 'starred-header'})}
          >
            Starred
          </div>
          {starredItems.map((item) => renderItem(item))}
        </div>
      )}
      <div className={cx(classes.recentSection, !fillHeight && classes.recentSectionNatural)}>
        <div
          className={classes.sectionHeader}
          {...getTestProps({component: 'GroupedChatListing', element: 'recent-header'})}
        >
          Recent
        </div>
        {/* In the sidebar only the Recent list scrolls; in the dropdown the whole list flows. */}
        <div className={cx(fillHeight && classes.recentScroll, fillHeight && 'scrollbar-override')}>
          {recentItems.map((item) => renderItem(item))}
          {/* Keep the affordance mounted while a subsequent page loads (hasMore is momentarily false
              during the pending fetch) so it shows a loading state rather than disappearing. */}
          {(hasMore || isLoadingMore) && (
            <UnstyledButton
              className={classes.viewMore}
              disabled={isLoadingMore}
              onClick={() => loadMore?.()}
              {...getTestProps({component: 'GroupedChatListing', element: 'view-more'})}
            >
              {isLoadingMore ? 'Loading…' : 'View more'}
            </UnstyledButton>
          )}
        </div>
      </div>
    </div>
  );
}
